import { extractErrorMessage } from '@/api/extractErrorMessage';
import pipelineKeys from '@/api/queries/pipelines/keyFactory';
import { createQueryClientCaches } from '@/api/queryClientDefaults';
import { GetPipelineQuery } from '@/gql/graphql';
import { useToastContext } from '@payaca/ambrosia';
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useMutationState,
  useQueryClient,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Prompt } from 'react-router-dom';

export const DRAFT_MUTATION_SCOPE_ID = 'pipeline-config-draft';

type DraftModePhase =
  | 'idle'
  | 'committing'
  | 'committing-stale'
  | 'cancelling'
  | 'cancelling-stale';

type DraftModeState = {
  phase: DraftModePhase;
  totalQueuedMutations: number;
};

type DraftModeAction =
  | { type: 'START_COMMIT'; pendingCount: number }
  | { type: 'MARK_STALE' }
  | { type: 'START_CANCEL' }
  | { type: 'RESET' };

function draftModeReducer(
  state: DraftModeState,
  action: DraftModeAction
): DraftModeState {
  switch (action.type) {
    case 'START_COMMIT':
      return { phase: 'committing', totalQueuedMutations: action.pendingCount };
    case 'START_CANCEL':
      return { ...state, phase: 'cancelling' };
    case 'MARK_STALE':
      if (state.phase === 'committing')
        return { ...state, phase: 'committing-stale' };
      if (state.phase === 'cancelling')
        return { ...state, phase: 'cancelling-stale' };
      return state;
    case 'RESET':
      return { phase: 'idle', totalQueuedMutations: 0 };
  }
}

const initialDraftModeState: DraftModeState = {
  phase: 'idle',
  totalQueuedMutations: 0,
};

type DraftModeContextValue = {
  phase: DraftModePhase;
  pendingCount: number;
  totalQueuedMutations: number;
  commit: () => Promise<void>;
  cancel: () => Promise<void>;
};

const DraftModeContext = createContext<DraftModeContextValue | null>(null);

export const useDraftMode = (): DraftModeContextValue => {
  const context = useContext(DraftModeContext);
  if (!context) {
    throw new Error('useDraftMode must be used within a DraftModeProvider');
  }
  return context;
};

type GateRef = {
  resolve: () => void;
  reject: () => void;
};

type DraftModeProviderProps = PropsWithChildren<{
  pipelineId: string;
}>;

export const DraftModeProvider: FC<DraftModeProviderProps> = ({
  pipelineId,
  children,
}) => {
  const mainQueryClient = useQueryClient();
  const [state, dispatch] = useReducer(draftModeReducer, initialDraftModeState);
  const gateRef = useRef<GateRef | null>(null);

  const { pushError } = useToastContext();
  const pushErrorRef = useRef(pushError);

  useEffect(() => {
    pushErrorRef.current = pushError;
  }, [pushError]);

  // Create draft QueryClient seeded with pipeline data (stable for component lifetime)
  const [draftQueryClient] = useState<QueryClient>(() => {
    const client = createDraftQueryClient(pushErrorRef);
    const pipelineData = mainQueryClient.getQueryData<GetPipelineQuery>(
      pipelineKeys.pipeline(pipelineId)
    );
    if (pipelineData) {
      client.setQueryData(pipelineKeys.pipeline(pipelineId), pipelineData);
    }
    return client;
  });

  // Track pending mutations using useMutationState (reactive, no manual subscription)
  const pendingMutations = useMutationState(
    {
      filters: { status: 'pending' },
      select: (mutation) => ({
        mutationKey: mutation.options.mutationKey,
        scope: mutation.options.scope,
      }),
    },
    draftQueryClient
  );

  // Filter out the gate mutation from the count
  const pendingCount = pendingMutations.filter((key) => {
    return (
      key.scope?.id === DRAFT_MUTATION_SCOPE_ID &&
      key.mutationKey?.[0] !== 'gate-mutation'
    );
  }).length;

  // Start gate mutation - this blocks all scoped mutations until resolved
  const { mutateAsync: startGate } = useMutation(
    {
      mutationKey: ['gate-mutation'],
      mutationFn: () => {
        return new Promise<void>((resolve, reject) => {
          gateRef.current = { resolve, reject };
        }).catch(() => {
          // No-op on reject since we handle cancellation via mutation cache clearing
        });
      },
      scope: { id: DRAFT_MUTATION_SCOPE_ID },
      meta: {
        skipGlobalErrorToast: true,
      },
    },
    draftQueryClient
  );

  // Start gate mutation on mount
  useEffect(() => {
    void startGate();

    return () => {
      // Clear all mutations BEFORE rejecting the gate to prevent
      // queued mutations from being flushed on unmount
      draftQueryClient.getMutationCache().clear();
      // Clear query cache too so the orphaned client can be GC'd immediately
      // (otherwise queries linger for the default 5min gcTime)
      draftQueryClient.clear();
      gateRef.current?.reject();
      gateRef.current = null;
    };
  }, [startGate, draftQueryClient]);

  const commit = useCallback(async () => {
    dispatch({ type: 'START_COMMIT', pendingCount });

    const timeoutId = setTimeout(() => {
      dispatch({ type: 'MARK_STALE' });
    }, 500);

    try {
      // Resolve the gate to allow queued mutations to proceed
      gateRef.current?.resolve();

      // Wait for all mutations to complete
      await draftQueryClient.getMutationCache().resumePausedMutations();

      // Clear completed mutations from draft client (resets scope tracking)
      draftQueryClient.getMutationCache().clear();

      // Refetch fresh data from server on the draft client
      await draftQueryClient.refetchQueries({
        queryKey: pipelineKeys.pipeline(pipelineId),
      });

      // Invalidate main cache so other parts of the app see updated data
      void mainQueryClient.invalidateQueries({
        queryKey: pipelineKeys.pipeline(pipelineId),
      });
      void mainQueryClient.invalidateQueries({
        queryKey: pipelineKeys.all,
      });

      gateRef.current = null;

      // Restart the gate mutation on the same client
      void startGate();
    } catch (error) {
      const { message } = extractErrorMessage(error);
      pushErrorRef.current(message);
    } finally {
      clearTimeout(timeoutId);
      dispatch({ type: 'RESET' });
    }
  }, [pendingCount, draftQueryClient, mainQueryClient, pipelineId, startGate]);

  const cancel = useCallback(async () => {
    dispatch({ type: 'START_CANCEL' });

    const timeoutId = setTimeout(() => {
      dispatch({ type: 'MARK_STALE' });
    }, 500);

    try {
      // Clear all mutations from the draft client (resets scope tracking)
      // this includes removing the gate
      draftQueryClient.getMutationCache().clear();

      // Refetch fresh data from server on the draft client
      await draftQueryClient.refetchQueries({
        queryKey: pipelineKeys.pipeline(pipelineId),
      });

      // Invalidate main cache so other parts of the app see current data
      void mainQueryClient.invalidateQueries({
        queryKey: pipelineKeys.pipeline(pipelineId),
      });

      gateRef.current = null;

      // Restart the gate mutation on the same client
      void startGate();
    } catch (error) {
      const { message } = extractErrorMessage(error);
      pushErrorRef.current(message);
    } finally {
      clearTimeout(timeoutId);
      dispatch({ type: 'RESET' });
    }
  }, [draftQueryClient, mainQueryClient, pipelineId, startGate]);

  // Navigation warning when there are unsaved changes
  const hasUnsavedChanges = pendingCount > 0;

  // Warn on browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const contextValue = useMemo(
    (): DraftModeContextValue => ({
      phase: state.phase,
      totalQueuedMutations: state.totalQueuedMutations,
      pendingCount,
      commit,
      cancel,
    }),
    [state, pendingCount, commit, cancel]
  );

  return (
    <DraftModeContext.Provider value={contextValue}>
      <QueryClientProvider client={draftQueryClient}>
        <Prompt
          when={hasUnsavedChanges}
          message="You have unsaved changes. Are you sure you want to leave?"
        />
        {children}

        <ReactQueryDevtools buttonPosition="bottom-left" />
      </QueryClientProvider>
    </DraftModeContext.Provider>
  );
};

/**
 * Separate QueryClient so all queries inside DraftModeProvider automatically get
 * draft-safe defaults (no background refetching). Without this, each query/mutation
 * hook would need these options individually.
 *
 * Uses the shared global error toast handlers so mutation/query errors surface to users.
 */
function createDraftQueryClient(pushErrorRef: {
  current: (message: string) => void;
}): QueryClient {
  return new QueryClient({
    ...createQueryClientCaches(pushErrorRef),
    defaultOptions: {
      queries: {
        // Disable automatic refetching for draft mode
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        staleTime: Infinity,
      },
    },
  });
}
